import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  onSnapshot,
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject
} from 'firebase/storage';
import { db, storage } from '../firebase';
import { useServiceList } from './useServiceList'; // Add this import

export const useGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
    landingPage: 'all',
    search: ''
  });

  // Get services from Firestore for categories
  const { services } = useServiceList();

  // Dynamic categories based on services + Uncategorized - memoized to prevent re-creation
  const categories = useMemo(() => {
    return [
      ...services.map(service => ({
        id: service.name.toLowerCase().replace(/\s+/g, '-'),
        name: service.name,
        count: 0
      })),
      { id: 'uncategorized', name: 'Uncategorized', count: 0 }
    ];
  }, [services]);

  // Helper function to generate thumbnails
  const generateThumbnail = (file, maxWidth = 300, maxHeight = 300, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Update category counts based on images
  const updateCategoryCounts = useCallback((imagesList) => {
    const categoryCounts = {};
    
    // Initialize counts
    categories.forEach(cat => {
      categoryCounts[cat.name] = 0;
    });
    
    // Count images in each category
    imagesList.forEach(image => {
      const category = image.category || 'Uncategorized';
      if (categoryCounts.hasOwnProperty(category)) {
        categoryCounts[category]++;
      }
    });
    
    // Update categories with counts (this is mainly for display purposes)
    // The actual categories array is already managed by the services from Firestore
  }, [categories]);

  // Load gallery data from Firestore
  const loadGalleryData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const imagesRef = collection(db, 'gallery_images');
      const q = query(imagesRef, orderBy('uploadedAt', 'desc'));
      
      // Set up real-time listener
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const imagesList = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          imagesList.push({
            id: doc.id,
            ...data,
            uploadedAt: data.uploadedAt?.toDate() || new Date()
          });
        });
        
        setImages(imagesList);
        updateCategoryCounts(imagesList);
        setLoading(false);
      });

      return unsubscribe;
    } catch (err) {
      setError('Failed to load gallery data. Please try again.');
      console.error('Gallery load error:', err);
      setLoading(false);
    }
  }, [updateCategoryCounts]);

  // Initialize gallery data
  useEffect(() => {
    const unsubscribe = loadGalleryData();
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [loadGalleryData]);

  // Upload multiple images
  const uploadImages = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Create unique filename
        const timestamp = Date.now();
        const filename = `${timestamp}-${file.name.toLowerCase().replace(/[^a-z0-9.-]/g, '')}`;
        
        // Upload main image
        const imageRef = ref(storage, `gallery/${filename}`);
        const uploadResult = await uploadBytes(imageRef, file);
        const imageUrl = await getDownloadURL(uploadResult.ref);
        
        // Generate and upload thumbnail
        const thumbnailBlob = await generateThumbnail(file);
        const thumbnailRef = ref(storage, `gallery/thumbnails/${filename}`);
        const thumbnailUpload = await uploadBytes(thumbnailRef, thumbnailBlob);
        const thumbnailUrl = await getDownloadURL(thumbnailUpload.ref);

        // Get image dimensions
        const img = new Image();
        const dimensions = await new Promise((resolve) => {
          img.onload = () => resolve(`${img.width}x${img.height}`);
          img.src = imageUrl;
        });

        // Save metadata to Firestore
        const imageData = {
          title: file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, ' '),
          description: '',
          url: imageUrl,
          thumbnail: thumbnailUrl,
          filename: filename,
          originalName: file.name,
          category: 'Uncategorized',  // Default to Uncategorized
          tags: [],
          uploadedAt: new Date(),
          size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
          dimensions: dimensions,
          isActive: true,
          onLandingPage: false,
          landingSection: null,
          alt: file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, ' ')
        };

        const docRef = await addDoc(collection(db, 'gallery_images'), imageData);
        return { id: docRef.id, ...imageData };
      });

      await Promise.all(uploadPromises);
    } catch (err) {
      setError('Failed to upload images. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  // Update image metadata
  const updateImage = async (imageId, updates) => {
    try {
      const imageRef = doc(db, 'gallery_images', imageId);
      await updateDoc(imageRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (err) {
      setError('Failed to update image. Please try again.');
      console.error('Update error:', err);
    }
  };

  // Delete images
  const deleteImages = async (imageIds) => {
    try {
      const deletePromises = imageIds.map(async (imageId) => {
        // Get image data first
        const image = images.find(img => img.id === imageId);
        if (!image) return;

        // Delete from Storage
        const imageRef = ref(storage, `gallery/${image.filename}`);
        const thumbnailRef = ref(storage, `gallery/thumbnails/${image.filename}`);
        
        await Promise.all([
          deleteObject(imageRef),
          deleteObject(thumbnailRef)
        ]);

        // Delete from Firestore
        await deleteDoc(doc(db, 'gallery_images', imageId));
      });

      await Promise.all(deletePromises);
    } catch (err) {
      setError('Failed to delete images. Please try again.');
      console.error('Delete error:', err);
    }
  };

  // Toggle landing page status
  const toggleLandingPage = async (imageId) => {
    const image = images.find(img => img.id === imageId);
    if (!image) return;

    await updateImage(imageId, {
      onLandingPage: !image.onLandingPage,
      landingSection: !image.onLandingPage ? null : image.landingSection
    });
  };

  // Bulk update images
  const bulkUpdateImages = async (imageIds, updates) => {
    try {
      const updatePromises = imageIds.map(imageId => updateImage(imageId, updates));
      await Promise.all(updatePromises);
    } catch (err) {
      setError('Failed to bulk update images. Please try again.');
      console.error('Bulk update error:', err);
    }
  };

  // Get filtered images
  const getFilteredImages = () => {
    return images.filter(image => {
      // Category filter
      if (filters.category !== 'all') {
        const filterCategory = filters.category.replace('-', ' ');
        const imageCategory = (image.category || 'Uncategorized').toLowerCase();
        if (imageCategory !== filterCategory.toLowerCase()) {
          return false;
        }
      }

      // Status filter
      if (filters.status !== 'all') {
        if (filters.status === 'active' && !image.isActive) return false;
        if (filters.status === 'inactive' && image.isActive) return false;
      }

      // Landing page filter
      if (filters.landingPage !== 'all') {
        if (filters.landingPage === 'assigned' && !image.onLandingPage) return false;
        if (filters.landingPage === 'unassigned' && image.onLandingPage) return false;
      }

      // Search filter
      if (filters.search && 
          !image.title?.toLowerCase().includes(filters.search.toLowerCase()) &&
          !image.description?.toLowerCase().includes(filters.search.toLowerCase()) &&
          !image.tags?.some(tag => tag.toLowerCase().includes(filters.search.toLowerCase()))) {
        return false;
      }

      return true;
    });
  };

  // Get images by landing page section
  const getImagesBySection = (sectionId) => {
    return images.filter(image => 
      image.onLandingPage && 
      image.landingSection === sectionId &&
      image.isActive
    );
  };

  // Get gallery statistics
  const getGalleryStats = () => {
    return {
      total: images.length,
      active: images.filter(img => img.isActive).length,
      inactive: images.filter(img => !img.isActive).length,
      onLandingPage: images.filter(img => img.onLandingPage).length,
      hero: images.filter(img => img.landingSection === 'hero').length,
      about: images.filter(img => img.landingSection === 'about').length,
      gallery: images.filter(img => img.landingSection === 'gallery').length
    };
  };

  // Update filters
  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  return {
    images,
    categories,
    loading,
    uploading,
    error,
    filters,
    uploadImages,
    updateImage,
    deleteImages,
    toggleLandingPage,
    bulkUpdateImages,
    getFilteredImages,
    getImagesBySection,
    getGalleryStats,
    updateFilters,
    clearError
  };
};